import * as React from 'react';
import {useShepherd} from 'react-shepherd';

import {offset} from '@floating-ui/dom';
import ReactDOMServer from 'react-dom/server';

import {analyticsClient} from '../../../../../analytics-client';
import {appHistory} from '../../../../../history';
import {useUpdateSetting} from '../../../../../ui/core/use-update-setting';

import {getTourConfig, getStepButtons} from './helpers';
import {TourStepContent} from './tour-step-content';
import type {NutshellTour} from './types';

type NutshellTourControls = {
    startTour: (tourId: NutshellTour) => void;
    exitTour: () => void;
    progressTour: () => void;
    pauseTour: () => void;
    resumeTour: () => void;
};

// https://docs.shepherdjs.dev/api/tour/interfaces/touroptions/
const DEFAULT_TOUR_OPTIONS = {
    useModalOverlay: true,
    keyboardNavigation: false,
    defaultStepOptions: {
        classes: 'nutshell-tour-step',
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8,
        scrollTo: {behavior: 'smooth', block: 'nearest'},
        floatingUIOptions: {
            middleware: [offset(16)],
        },
    },
} as const;
const COMPLETION_REDIRECT = '/explore';

// A hook to control the Nutshell tour system powered by Shepherd.js
// (react-shepherd). Allows you to start, exit, and progress through a tour.
// Also maintains some state about the current tour.
export function useNutshellTour(): NutshellTourControls {
    // Global Shepherd instance
    const Shepherd = useShepherd();
    const updateSetting = useUpdateSetting();

    const startTour = (tourId: NutshellTour) => {
        const tourConfig = getTourConfig(tourId);

        // @ts-expect-error
        const tour = new Shepherd.Tour({
            ...DEFAULT_TOUR_OPTIONS,
            id: tourConfig.id,
        });

        analyticsClient.track(analyticsClient.EVENTS.TRACK_NUTSHELL_TOUR_STARTED, {
            type: tourConfig.id,
        });

        // Track whether to redirect on complete (Close button = false, Back to Explore = true)
        let shouldRedirectOnComplete = true;

        const handleComplete = (shouldRedirect: boolean) => {
            shouldRedirectOnComplete = shouldRedirect;
            tour.complete();
        };

        const stepCleanups: Map<number, () => void> = new Map();

        tourConfig.steps.forEach((step, index) => {
            const isLastStep = index === tourConfig.steps.length - 1;

            const buttons = getStepButtons(
                step,
                isLastStep,
                () => {
                    tour.next();
                },
                handleComplete,
                tourConfig
            );

            const when: Record<string, () => void> = {};
            if (step.onShow) {
                when.show = () => {
                    const cleanup = step.onShow?.();
                    if (cleanup) {
                        stepCleanups.set(index, cleanup);
                    }
                };
                when.hide = () => {
                    stepCleanups.get(index)?.();
                    stepCleanups.delete(index);
                };
            }

            tour.addStep({
                id: `${tourConfig.id}-step-${index}`,
                text: ReactDOMServer.renderToString(
                    <TourStepContent
                        stepNumber={index + 1}
                        totalSteps={tourConfig.steps.length}
                        title={step.title}
                        description={step.description}
                        requiredAction={step.requiredAction}
                        illustration={step.illustration}
                    />
                ),
                buttons,
                when,
                attachTo: {
                    element: step.anchor && step.anchor.element,
                    on: (step.anchor && step.anchor.on) || 'bottom',
                },
                extraHighlights: step.extraHighlights,
                showOn: step.isCompletable ? step.isCompletable : undefined,
                cancelIcon: {
                    enabled: !isLastStep,
                },
            });
        });

        tour.on('complete', () => {
            stepCleanups.forEach((cleanup) => cleanup());
            stepCleanups.clear();

            analyticsClient.track(analyticsClient.EVENTS.TRACK_NUTSHELL_TOUR_COMPLETED, {
                type: tourConfig.id,
            });

            const completionSetting = tourConfig.completionSetting;

            if (completionSetting) {
                updateSetting({
                    settingName: completionSetting,
                    value: true,
                });
            }

            if (shouldRedirectOnComplete) {
                appHistory.push(COMPLETION_REDIRECT);
            }
        });

        tour.start();
    };

    const exitTour = React.useCallback(() => {
        if (Shepherd.activeTour) {
            Shepherd.activeTour.cancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const progressTour = React.useCallback(() => {
        if (Shepherd.activeTour) {
            Shepherd.activeTour.next();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helpful for hiding the tour step when completing a required action causes a loading state
    // which removes the tour anchor element
    const pauseTour = React.useCallback(() => {
        if (Shepherd.activeTour) {
            Shepherd.activeTour.hide();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resumeTour = React.useCallback(() => {
        const currentStep = Shepherd?.activeTour?.getCurrentStep();
        if (currentStep) {
            currentStep.show();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        startTour,
        exitTour,
        progressTour,
        pauseTour,
        resumeTour,
    };
}
